// Metrics-over-time for the pathway DSL.
//
// A DSL pattern + `measure:` produces, per matched episode, a MeasurementResult
// with a value and dates. This turns a bag of those into a time series: anchor
// each measurement to a date, bucket by month/quarter/year, aggregate per
// bucket. Optionally scope to a single trust (attributed per-episode). Pure and
// framework-free, like the rest of the DSL.
import {
  inBounds,
  type MeasurementBounds,
  type PathwayMatch,
} from "./pathwayDsl.js";

export type TimeBucket = "month" | "quarter" | "year";
export type Aggregator = "median" | "mean" | "sum" | "count";

export type TrendPoint = {
  bucket: Date; // start of the bucket period (UTC)
  value: number; // aggregated metric for the bucket
  n: number; // number of episodes in the bucket
  complete: number; // episodes whose `to` boundary occurred before asOf (vs still-ongoing)
  p25: number; // spread (useful for duration medians/means)
  p75: number;
};

export type TimeSeriesOptions = {
  bucket?: TimeBucket; // default "month"
  anchor?: "start" | "end"; // which measurement date to bucket on; default "start"
  aggregate?: Aggregator; // default: "sum" for count metrics, else "median"
  trustsOf?: (match: PathwayMatch) => string[]; // trusts an episode touched
  trustFilter?: string | null; // scope to a single trust (matches any touched)
  asOf?: Date; // "now" for completeness; default real clock
  windowStart?: Date; // only episodes anchored on/after this
  windowEnd?: Date; // only episodes anchored on/before this
  bounds?: MeasurementBounds; // plausibility clamp (drops junk dates)
  excludeExpired?: boolean; // drop system auto-lapsed (expired) episodes
};

export type SurvivalPoint = {
  bucket: Date; // start-cohort bucket (UTC)
  rate: number; // fraction still in-state at the horizon (0..1)
  n: number; // observable episodes in the cohort (mature enough to evaluate)
};

export type SurvivalOptions = {
  horizonDays: number; // the landmark H
  asOf?: Date; // default real clock
  bucket?: TimeBucket; // default "month"
  anchor?: "start" | "end"; // start cohort (landmark) or by end date; default "start"
  trustsOf?: (match: PathwayMatch) => string[];
  trustFilter?: string | null;
  windowStart?: Date; // only cohorts starting on/after this
  windowEnd?: Date; // only cohorts starting on/before this
  bounds?: MeasurementBounds; // plausibility clamp (drops junk dates)
  excludeExpired?: boolean; // drop system auto-lapsed (expired) episodes
};

/**
 * Every distinct trust an episode touched, across all its matched periods.
 * This is what scopes the trust filter: "involved this trust at any point",
 * not just the last — so a patient transferred between trusts is counted under
 * each one (and a trust that only ever feeds into another still appears in the
 * options). Sorted for stable option ordering.
 */
export function allTrustsOf(match: PathwayMatch): string[] {
  const set = new Set<string>();
  for (const step of match.stepMatches) {
    for (const period of step.periods) {
      period.trusts.forEach((t) => set.add(t));
    }
  }
  return Array.from(set).sort();
}

/** Truncate a date to the start of its month/quarter/year, in UTC. */
function bucketStart(date: Date, bucket: TimeBucket): Date {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth();
  if (bucket === "year") return new Date(Date.UTC(y, 0, 1));
  if (bucket === "quarter") return new Date(Date.UTC(y, m - (m % 3), 1));
  return new Date(Date.UTC(y, m, 1));
}

function aggregate(values: number[], how: Aggregator): number {
  if (how === "count") return values.length;
  if (how === "sum") return values.reduce((a, b) => a + b, 0);
  if (how === "mean") return values.reduce((a, b) => a + b, 0) / values.length;
  return percentile(values, 0.5);
}

/**
 * The value a fraction `p` of the way through the sample, sorted ascending. An
 * empty sample has no such value, so it yields NaN — the same answer `mean`
 * gives for one.
 */
function percentile(values: number[], p: number): number {
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length * p)] ?? NaN;
}

/**
 * Aggregate a named measurement across matches into a time series.
 * Negative values (data artifacts) are dropped, matching getMeasurementDistribution.
 */
export function getMeasurementTimeSeries(
  matches: PathwayMatch[],
  measurementName: string,
  opts: TimeSeriesOptions = {},
): TrendPoint[] {
  const bucket = opts.bucket ?? "month";
  const anchor = opts.anchor ?? "start";
  const trustsOf = opts.trustsOf ?? allTrustsOf;
  const asOf = (opts.asOf ?? new Date()).getTime();

  // Default aggregator follows the measurement's type.
  const sample = matches
    .flatMap((m) => m.measurements)
    .find((m) => m.name === measurementName);
  const how: Aggregator =
    opts.aggregate ?? (sample?.type === "count" ? "sum" : "median");

  const buckets = new Map<
    number,
    { date: Date; values: number[]; complete: number }
  >();

  for (const match of matches) {
    if (
      opts.trustFilter != null &&
      !trustsOf(match).includes(opts.trustFilter)
    ) {
      continue;
    }
    for (const m of match.measurements) {
      if (m.name !== measurementName || !inBounds(m, opts.bounds)) continue;
      if (opts.excludeExpired && m.endStatus === "expired") continue;
      // End-anchored views are about periods that have *ended*; a still-active
      // period's `to` is just the asOf cap, not a real end, so drop it.
      if (anchor === "end" && m.endStatus === "active") continue;
      const anchorDate = anchor === "end" ? m.toDate : m.fromDate;
      if (opts.windowStart && anchorDate < opts.windowStart) continue;
      if (opts.windowEnd && anchorDate > opts.windowEnd) continue;
      const start = bucketStart(anchorDate, bucket);
      const key = start.getTime();
      const entry = buckets.get(key) ?? {
        date: start,
        values: [],
        complete: 0,
      };
      entry.values.push(m.value);
      if (m.toDate.getTime() < asOf) entry.complete += 1;
      buckets.set(key, entry);
    }
  }

  return Array.from(buckets.values())
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .map(({ date, values, complete }) => ({
      bucket: date,
      value: aggregate(values, how),
      n: values.length,
      complete,
      p25: percentile(values, 0.25),
      p75: percentile(values, 0.75),
    }));
}

/**
 * Fraction of episodes reaching a horizon (value >= H), bucketed two ways:
 *
 *  - anchor "start" (default) — landmark survival per *start* cohort: of
 *    episodes that began in each bucket and are mature enough to evaluate
 *    (start + H <= asOf), the fraction still in-state at H days. Censoring-safe
 *    up to H; immature buckets are excluded (you genuinely can't know yet).
 *  - anchor "end" — of episodes that *ended* in each bucket, the fraction whose
 *    lifetime reached H days. No maturity gate (every episode has a real end),
 *    but still-active periods are dropped (their `to` is just the asOf cap, not
 *    a real end). Answers "of the detentions we're concluding now, how many ran
 *    long?" rather than the start cohort's "how many will run long?".
 *
 * Generic over any duration measure.
 */
export function getSurvivalSeries(
  matches: PathwayMatch[],
  measurementName: string,
  opts: SurvivalOptions,
): SurvivalPoint[] {
  const H = opts.horizonDays;
  const bucket = opts.bucket ?? "month";
  const anchor = opts.anchor ?? "start";
  const asOf = (opts.asOf ?? new Date()).getTime();
  const trustsOf = opts.trustsOf ?? allTrustsOf;
  const horizonMs = H * 24 * 60 * 60 * 1000;

  const buckets = new Map<
    number,
    { date: Date; observed: number; survived: number }
  >();

  for (const match of matches) {
    if (
      opts.trustFilter != null &&
      !trustsOf(match).includes(opts.trustFilter)
    ) {
      continue;
    }
    for (const m of match.measurements) {
      if (m.name !== measurementName || !inBounds(m, opts.bounds)) continue;
      if (opts.excludeExpired && m.endStatus === "expired") continue;
      const anchorDate = anchor === "end" ? m.toDate : m.fromDate;
      if (opts.windowStart && anchorDate < opts.windowStart) continue;
      if (opts.windowEnd && anchorDate > opts.windowEnd) continue;
      if (anchor === "end") {
        // End-anchored: only periods that have genuinely ended.
        if (m.endStatus === "active") continue;
      } else {
        // Start-cohort: only cohorts we've watched for at least H days.
        if (m.fromDate.getTime() + horizonMs > asOf) continue;
      }
      const start = bucketStart(anchorDate, bucket);
      const key = start.getTime();
      const entry = buckets.get(key) ?? {
        date: start,
        observed: 0,
        survived: 0,
      };
      entry.observed += 1;
      if (m.value >= H) entry.survived += 1;
      buckets.set(key, entry);
    }
  }

  return Array.from(buckets.values())
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .map(({ date, observed, survived }) => ({
      bucket: date,
      rate: observed === 0 ? 0 : survived / observed,
      n: observed,
    }));
}
